import { useState, useEffect } from 'react';
import { Database, Plus, Search, Edit, Trash2, Save, X, Loader2, Server, Laptop, Monitor, Wifi, Printer as PrinterIcon, Box } from 'lucide-react';
import { cmdbAPI } from '../services/api';
import { toast } from 'sonner';

const typeIcons = { server: Server, laptop: Laptop, workstation: Monitor, network_device: Wifi, printer: PrinterIcon, software: Box, virtual_machine: Server, storage: Database, mobile: Laptop, other: Box };
const typeColors = { server: 'bg-blue-100 text-blue-800', laptop: 'bg-emerald-100 text-emerald-800', workstation: 'bg-purple-100 text-purple-800', network_device: 'bg-amber-100 text-amber-800', printer: 'bg-slate-100 text-slate-700', software: 'bg-indigo-100 text-indigo-800', virtual_machine: 'bg-cyan-100 text-cyan-800', storage: 'bg-orange-100 text-orange-800' };
const statusColors = { active: 'bg-emerald-100 text-emerald-800', inactive: 'bg-slate-100 text-slate-600', maintenance: 'bg-amber-100 text-amber-800', retired: 'bg-rose-100 text-rose-800', disposed: 'bg-slate-200 text-slate-500' };

export default function CMDBPage() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ asset_tag: '', name: '', asset_type: 'server', status: 'active', model: '', serial_number: '', ip_address: '', location: '', notes: '' });

  const fetch = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (typeFilter) params.asset_type = typeFilter;
      const res = await cmdbAPI.listAssets(params);
      setAssets(res.data?.results || res.data || []);
    } catch { setAssets([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); // eslint-disable-next-line
  }, []);

  const handleSave = async () => {
    if (!form.asset_tag || !form.name) { toast.error('Asset tag and name required'); return; }
    try {
      if (editing) { await cmdbAPI.updateAsset(editing, form); toast.success('Asset updated'); }
      else { await cmdbAPI.createAsset(form); toast.success('Asset created'); }
      setShowForm(false); setEditing(null);
      setForm({ asset_tag: '', name: '', asset_type: 'server', status: 'active', model: '', serial_number: '', ip_address: '', location: '', notes: '' });
      fetch();
    } catch (err) { toast.error(JSON.stringify(err.response?.data) || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this asset?')) return;
    try { await cmdbAPI.deleteAsset(id); toast.success('Deleted'); fetch(); }
    catch { toast.error('Failed'); }
  };

  const startEdit = (a) => {
    setForm({ asset_tag: a.asset_tag, name: a.name, asset_type: a.asset_type, status: a.status, model: a.model || '', serial_number: a.serial_number || '', ip_address: a.ip_address || '', location: a.location || '', notes: a.notes || '' });
    setEditing(a.id); setShowForm(true);
  };

  const typeCounts = {};
  assets.forEach(a => { typeCounts[a.asset_type] = (typeCounts[a.asset_type] || 0) + 1; });

  return (
    <div data-testid="cmdb-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <Database className="w-6 h-6 text-sky-500" /> CMDB - Configuration Management
          </h2>
          <p className="text-sm text-slate-500 mt-1">Track and manage IT assets, hardware, and software</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditing(null); setForm({ asset_tag: '', name: '', asset_type: 'server', status: 'active', model: '', serial_number: '', ip_address: '', location: '', notes: '' }); }}
          className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600" data-testid="add-asset-btn">
          <Plus className="w-4 h-4" /> Add Asset
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border shadow-sm border-l-4 border-l-sky-500 cursor-pointer" onClick={() => { setTypeFilter(''); fetch(); }}>
          <div className="text-2xl font-bold text-sky-600">{assets.length}</div><div className="text-xs text-slate-500">Total Assets</div>
        </div>
        {['server', 'laptop', 'network_device', 'software'].map(t => (
          <div key={t} onClick={() => { setTypeFilter(t); fetch(); }} className="bg-white dark:bg-slate-800 p-4 rounded-xl border shadow-sm cursor-pointer hover:border-sky-300">
            <div className="text-2xl font-bold text-slate-800 dark:text-white">{typeCounts[t] || 0}</div>
            <div className="text-xs text-slate-500 capitalize">{t.replace(/_/g, ' ')}s</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetch()} placeholder="Search assets..." className="w-full pl-9 pr-3 py-2.5 border rounded-xl text-sm bg-white dark:bg-slate-800 dark:text-white dark:border-slate-700" data-testid="search-assets" />
        </div>
        <button onClick={fetch} className="bg-white dark:bg-slate-800 border dark:border-slate-700 px-4 py-2 rounded-xl text-sm hover:bg-slate-50">Search</button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-6 mb-6">
          <h3 className="font-semibold mb-4 dark:text-white">{editing ? 'Edit' : 'New'} Asset</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <input placeholder="Asset Tag *" value={form.asset_tag} onChange={e => setForm({...form, asset_tag: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" />
            <input placeholder="Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" />
            <select value={form.asset_type} onChange={e => setForm({...form, asset_type: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white">
              <option value="server">Server</option><option value="laptop">Laptop</option><option value="workstation">Workstation</option>
              <option value="network_device">Network Device</option><option value="printer">Printer</option><option value="software">Software</option>
              <option value="virtual_machine">Virtual Machine</option><option value="storage">Storage</option><option value="mobile">Mobile</option>
            </select>
            <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white">
              <option value="active">Active</option><option value="inactive">Inactive</option><option value="maintenance">Maintenance</option><option value="retired">Retired</option>
            </select>
            <input placeholder="Model" value={form.model} onChange={e => setForm({...form, model: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" />
            <input placeholder="IP Address" value={form.ip_address} onChange={e => setForm({...form, ip_address: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" />
            <input placeholder="Serial Number" value={form.serial_number} onChange={e => setForm({...form, serial_number: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" />
            <input placeholder="Location" value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" />
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleSave} className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-1.5 hover:bg-sky-600"><Save className="w-4 h-4" /> Save</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-4 py-2 rounded-xl text-sm flex items-center gap-1.5"><X className="w-4 h-4" /> Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 overflow-hidden shadow-sm">
          <table className="w-full" data-testid="assets-table">
            <thead className="bg-slate-50 dark:bg-slate-900 text-xs text-slate-600 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Asset Tag</th>
                <th className="px-4 py-3 text-left font-semibold">Name</th>
                <th className="px-4 py-3 text-center font-semibold">Type</th>
                <th className="px-4 py-3 text-center font-semibold">Status</th>
                <th className="px-4 py-3 text-left font-semibold">Model</th>
                <th className="px-4 py-3 text-left font-semibold">IP Address</th>
                <th className="px-4 py-3 text-left font-semibold">Location</th>
                <th className="px-4 py-3 text-left font-semibold">Assigned To</th>
                <th className="px-4 py-3 text-center font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-slate-700 text-sm">
              {assets.map(a => (
                <tr key={a.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                  <td className="px-4 py-3 font-mono font-semibold text-sky-700 dark:text-sky-400">{a.asset_tag}</td>
                  <td className="px-4 py-3 font-medium dark:text-white">{a.name}</td>
                  <td className="px-4 py-3 text-center"><span className={`${typeColors[a.asset_type] || 'bg-slate-100'} px-2 py-1 rounded-full text-xs font-medium capitalize`}>{a.asset_type?.replace(/_/g, ' ')}</span></td>
                  <td className="px-4 py-3 text-center"><span className={`${statusColors[a.status] || 'bg-slate-100'} px-2 py-1 rounded-full text-xs`}>{a.status}</span></td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{a.model || '-'}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-400">{a.ip_address || '-'}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{a.location || '-'}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{a.assigned_to_name || '-'}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center gap-1">
                      <button onClick={() => startEdit(a)} className="text-slate-400 hover:text-sky-600 p-1"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(a.id)} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {assets.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">No assets found</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
